// src/context/GoogleAdsContext.js

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './auth';
import { usePermissions } from './permissions';
import { cacheData, getCachedData } from '../utils/ads/cacheData';
import { extractDateFromBigQueryTimestamp } from '../utils/ads/helpers';
import {
	fetchUsersFromApi,
	fetchInvitationsFromApi,
	fetchTopLevelAccountsFromApi,
	fetchAdsManagedAccountsFromApi,
	fetchAuditLogsFromApi,
	fetchMyAccessFromApi,
	fetchPendingRequestsFromApi,
	fetchApprovedRequestsFromApi,
	fetchRejectedRequestsFromApi
} from '../utils/ads/adsApi';


// Create context
const GoogleAdsContext = createContext();

// Context provider component
export const GoogleAdsProvider = ({ children }) => {
	// Get auth context
	const { isAuthenticated, user } = useAuth();

	// Check if user has ads admin access
	const { hasPermission, loading: permissionsLoading } = usePermissions();
	const hasAdsPermission = hasPermission('admin/ads');

	
	// State for users in BigQuery
	const [users, setUsers] = useState([]);
	const [usersLoaded, setUsersLoaded] = useState(false);
	const [usersError, setUsersError] = useState(null);
	

	// State for pending invitations
	const [invitations, setInvitations] = useState([]);
	const [invitationsLoaded, setInvitationsLoaded] = useState(false);
	const [invitationsError, setInvitationsError] = useState(null);

	// State for top-level MCC accounts
	const [topLevelMccAccounts, setTopLevelMccAccounts] = useState([]);
	const [topLevelMccAccountsLoaded, setTopLevelMccAccountsLoaded] = useState(false);
	const [topLevelMccAccountsError, setTopLevelMccAccountsError] = useState(null);

	// State for managed accounts (ads_managed_accounts)
	const [adsManagedAccounts, setAdsManagedAccounts] = useState([]);
	const [adsManagedAccountsLoaded, setAdsManagedAccountsLoaded] = useState(false);
	const [adsManagedAccountsError, setAdsManagedAccountsError] = useState(null);
	
	// State for audit logs
	const [auditLogs, setAuditLogs] = useState([]);
	const [auditLogsLoaded, setAuditLogsLoaded] = useState(false);
	const [auditLogsError, setAuditLogsError] = useState(null);
	
	// State for my access (filtered users data)
	const [myAccess, setMyAccess] = useState([]);
	const [myAccessLoaded, setMyAccessLoaded] = useState(false);
	const [myAccessError, setMyAccessError] = useState(null);
	
	// State for pending requests
	const [pendingRequests, setPendingRequests] = useState([]);
	const [pendingRequestsLoaded, setPendingRequestsLoaded] = useState(false);
	const [pendingRequestsError, setPendingRequestsError] = useState(null);
	
	// State for approved requests
	const [approvedRequests, setApprovedRequests] = useState([]);
	const [approvedRequestsLoaded, setApprovedRequestsLoaded] = useState(false);
	const [approvedRequestsError, setApprovedRequestsError] = useState(null);
	
	// State for rejected requests
	const [rejectedRequests, setRejectedRequests] = useState([]);
	const [rejectedRequestsLoaded, setRejectedRequestsLoaded] = useState(false);
	const [rejectedRequestsError, setRejectedRequestsError] = useState(null);
	
	// State to track context initialization
	const [initialized, setInitialized] = useState(false);
	
	// Refs to prevent duplicate API calls
	const isLoadingUsers = useRef(false);
	const isLoadingInvitations = useRef(false);
	const isLoadingTopLevelAccounts = useRef(false);
	const isLoadingAdsManagedAccounts = useRef(false);
	const isLoadingAuditLogs = useRef(false);
	const isLoadingMyAccess = useRef(false);
	const isLoadingPendingRequests = useRef(false);
	const isLoadingApprovedRequests = useRef(false);
	const isLoadingRejectedRequests = useRef(false);
	// Track if initial data loading has happened
	const hasLoadedInitialData = useRef(false);
	
	// Function to fetch users from BigQuery
	const fetchUsers = useCallback(async (forceRefresh = false) => {
		// Prevent concurrent requests
		if (isLoadingUsers.current) {
			return users;
		}
		
		// Check if we already have the data and can return it
		if (usersLoaded && users !== null && !forceRefresh) {
			return users;
		}
		
		// Check cache if not forcing refresh
		if (!forceRefresh) {
			const cachedUsers = getCachedData('googleAdsUsers');
			if (cachedUsers && cachedUsers.length > 0) {
				setUsers(cachedUsers);
				setUsersLoaded(true);
				setUsersError(null);
				return cachedUsers;
			}
		}
		
		// Set loading flag
		isLoadingUsers.current = true;
		
		// Track performance
		const startTime = performance.now();
		
		try {
			const userData = await fetchUsersFromApi(forceRefresh);
			
			// Log performance
			const duration = performance.now() - startTime;
			console.log(`GoogleAdsContext: Fetched ${userData.length} users in ${duration.toFixed(2)}ms`);
			
			// Update state
			setUsers(userData);
			setUsersLoaded(true);
			setUsersError(null);
			
			// Cache the data
			cacheData('googleAdsUsers', userData);
			
			return userData;
		} catch (error) {
			if (error.response) {
				console.error(`GoogleAdsContext: Error fetching users (${error.response.status}):`, 
					error.response.data?.error?.text || error.response.data?.message || error.message);
			} else if (error.request) {
				console.error('GoogleAdsContext: Error fetching users - No response received:', error.message);
			} else {
				console.error('GoogleAdsContext: Error fetching users:', error.message);
			}
			
			setUsersError(error.response?.data?.error?.text || error.message || 'Failed to fetch users data');
			return [];
		} finally {
			isLoadingUsers.current = false;
		}
	}, [users, usersLoaded]);
	
	
	// Function to fetch pending invitations
	const fetchInvitations = useCallback(async (forceRefresh = false) => {
		// Prevent concurrent requests
		if (isLoadingInvitations.current) {
			return invitations;
		}
		
		// Check if we already have the data and can return it
		if (invitationsLoaded && invitations !== null && !forceRefresh) {
			return invitations;
		}
		
		// Check cache if not forcing refresh
		if (!forceRefresh) {
			const cachedInvitations = getCachedData('googleAdsInvitations');
			if (cachedInvitations && cachedInvitations.length > 0) {
				setInvitations(cachedInvitations);
				setInvitationsLoaded(true);
				setInvitationsError(null);
				return cachedInvitations;
			}
		}
		
		// Set loading flag
		isLoadingInvitations.current = true;
		
		// Track performance
		const startTime = performance.now();
		
		try {
			// Use the correct API endpoint and parameters
			if (!user?.email) {
				throw new Error('User email not available');
			}
			
			const customerId = '8757783402'; // Default customer ID from original code
			const requestingUser = user.email;
			
			// Use the imported function from adsApi.js
			const invitationsData = await fetchInvitationsFromApi(customerId, requestingUser, forceRefresh);
			
			// Log performance
			const duration = performance.now() - startTime;
			console.log(`GoogleAdsContext: Fetched invitations data in ${duration.toFixed(2)}ms`);
			
			// Ensure invitationsData is an array before trying to map over it
			if (!Array.isArray(invitationsData)) {
				console.error('GoogleAdsContext: invitationsData is not an array:', typeof invitationsData);
				
				// Handle non-array gracefully
				setInvitations([]);
				setInvitationsLoaded(true);
				setInvitationsError('Received invalid data format for invitations');
				return [];
			}
			
			console.log(`GoogleAdsContext: Processing ${invitationsData.length} pending invitations`);
			
			// Process the data - normalize field names to match expected schema
			// Schema: customer_id, customer_name, email_address, invitation_id, access_role, status, created, manager_name, account_type
			const processedInvitations = invitationsData.map(invitation => ({
				...invitation,
				// Ensure consistent field names
				customer_id: invitation.customer_id,
				customer_name: invitation.customer_name,
				email_address: invitation.email_address,
				invitation_id: invitation.invitation_id,
				access_role: invitation.access_role,
				status: invitation.status,
				created: invitation.created,
				manager_name: invitation.manager_name,
				account_type: invitation.account_type
			}));
			
			// Update state
			setInvitations(processedInvitations);
			setInvitationsLoaded(true);
			setInvitationsError(null);

			// Cache the data
			cacheData('googleAdsInvitations', processedInvitations);

			return processedInvitations;
		} catch (error) {
			if (error.response) {
				console.error(`GoogleAdsContext: Error fetching pending invitations (${error.response.status}):`, 
					error.response.data?.error?.text || error.response.data?.message || error.message);
			} else if (error.request) {
				console.error('GoogleAdsContext: Error fetching pending invitations - No response received:', error.message);
			} else {
				console.error('GoogleAdsContext: Error fetching pending invitations:', error.message);
			}
			
			setInvitationsError(error.response?.data?.error?.text || error.message || 'Failed to fetch pending invitations');
			return [];
		} finally {
			isLoadingInvitations.current = false;
		}
	}, [invitations, invitationsLoaded, user]);

	// Function to fetch top-level MCC accounts
	const fetchTopLevelMccAccounts = useCallback(async (forceRefresh = false) => {
		// Prevent concurrent requests
		if (isLoadingTopLevelAccounts.current) {
			return topLevelMccAccounts;
		}
		
		// Check if we already have the data and can return it
		if (topLevelMccAccountsLoaded && topLevelMccAccounts !== null && !forceRefresh) {
			return topLevelMccAccounts;
		}
		
		// Check cache if not forcing refresh
		if (!forceRefresh) {
			const cachedAccounts = getCachedData('googleAdsTopLevelAccounts');
			if (cachedAccounts && cachedAccounts.length > 0) {
				setTopLevelMccAccounts(cachedAccounts);
				setTopLevelMccAccountsLoaded(true);
				setTopLevelMccAccountsError(null);
				return cachedAccounts;
			}
		}
		
		// Set loading flag
		isLoadingTopLevelAccounts.current = true;
		
		// Track performance
		const startTime = performance.now();
		
		try {
			const accountsData = await fetchTopLevelAccountsFromApi(forceRefresh);
			
			// Log performance
			const duration = performance.now() - startTime;
			console.log(`GoogleAdsContext: Fetched ${accountsData.length} top-level MCC accounts in ${duration.toFixed(2)}ms`);
			
			// Update state
			setTopLevelMccAccounts(accountsData);
			setTopLevelMccAccountsLoaded(true);
			setTopLevelMccAccountsError(null);
			
			// Cache the data
			cacheData('googleAdsTopLevelAccounts', accountsData);
			
			return accountsData;
		} catch (error) {
			if (error.response) {
				console.error(`GoogleAdsContext: Error fetching top-level MCC accounts (${error.response.status}):`, 
					error.response.data?.error?.text || error.response.data?.message || error.message);
			} else if (error.request) {
				console.error('GoogleAdsContext: Error fetching top-level MCC accounts - No response received:', error.message);
			} else {
				console.error('GoogleAdsContext: Error fetching top-level MCC accounts:', error.message);
			}
			
			setTopLevelMccAccountsError(error.response?.data?.error?.text || error.message || 'Failed to fetch top-level MCC accounts');
			return [];
		} finally {
			isLoadingTopLevelAccounts.current = false;
		}
	}, [topLevelMccAccounts, topLevelMccAccountsLoaded]);

	// Function to fetch managed accounts (ads_managed_accounts)
	const fetchAdsManagedAccounts = useCallback(async (forceRefresh = false) => {
		if (isLoadingAdsManagedAccounts.current) {
			return adsManagedAccounts;
		}

		if (adsManagedAccountsLoaded && adsManagedAccounts !== null && !forceRefresh) {
			return adsManagedAccounts;
		}

		if (!forceRefresh) {
			const cached = getCachedData('googleAdsManagedAccounts');
			if (cached && cached.length > 0) {
				setAdsManagedAccounts(cached);
				setAdsManagedAccountsLoaded(true);
				setAdsManagedAccountsError(null);
				return cached;
			}
		}

		isLoadingAdsManagedAccounts.current = true;
		const startTime = performance.now();

		try {
			const data = await fetchAdsManagedAccountsFromApi(forceRefresh);
			const duration = performance.now() - startTime;
			console.log(`GoogleAdsContext: Fetched ${data.length} managed accounts in ${duration.toFixed(2)}ms`);

			setAdsManagedAccounts(data);
			setAdsManagedAccountsLoaded(true);
			setAdsManagedAccountsError(null);
			cacheData('googleAdsManagedAccounts', data);
			return data;
		} catch (error) {
			if (error.response) {
				console.error(`GoogleAdsContext: Error fetching managed accounts (${error.response.status}):`,
					error.response.data?.error?.text || error.response.data?.message || error.message);
			} else if (error.request) {
				console.error('GoogleAdsContext: Error fetching managed accounts - No response received:', error.message);
			} else {
				console.error('GoogleAdsContext: Error fetching managed accounts:', error.message);
			}

			setAdsManagedAccountsError(error.response?.data?.error?.text || error.message || 'Failed to fetch managed accounts');
			return [];
		} finally {
			isLoadingAdsManagedAccounts.current = false;
		}
	}, [adsManagedAccounts, adsManagedAccountsLoaded]);
	
	// Function to fetch audit logs
	const fetchAuditLogs = useCallback(async (forceRefresh = false) => {
		// Prevent concurrent requests
		if (isLoadingAuditLogs.current) {
			return auditLogs;
		}
		
		// Check if we already have the data and can return it
		if (auditLogsLoaded && auditLogs !== null && !forceRefresh) {
			return auditLogs;
		}
		
		// Check cache if not forcing refresh
		if (!forceRefresh) {
			const cachedLogs = getCachedData('googleAdsAuditLogs');
			if (cachedLogs && cachedLogs.length > 0) {
				setAuditLogs(cachedLogs);
				setAuditLogsLoaded(true);
				setAuditLogsError(null);
				return cachedLogs;
			}
		}
		
		// Set loading flag
		isLoadingAuditLogs.current = true;
		
		// Track performance
		const startTime = performance.now();
		
		try {
			const logsData = await fetchAuditLogsFromApi(forceRefresh);
			
			// Log performance
			const duration = performance.now() - startTime;
			console.log(`GoogleAdsContext: Fetched ${logsData.length} audit logs in ${duration.toFixed(2)}ms`);
			
			// Update state
			setAuditLogs(logsData);
			setAuditLogsLoaded(true);
			setAuditLogsError(null);
			
			// Cache the data
			cacheData('googleAdsAuditLogs', logsData);
			
			return logsData;
		} catch (error) {
			if (error.response) {
				console.error(`GoogleAdsContext: Error fetching audit logs (${error.response.status}):`, 
					error.response.data?.error?.text || error.response.data?.message || error.message);
			} else if (error.request) {
				console.error('GoogleAdsContext: Error fetching audit logs - No response received:', error.message);
			} else {
				console.error('GoogleAdsContext: Error fetching audit logs:', error.message);
			}
			
			setAuditLogsError(error.response?.data?.error?.text || error.message || 'Failed to fetch audit logs');
			return [];
		} finally {
			isLoadingAuditLogs.current = false;
		}
	}, [auditLogs, auditLogsLoaded]);
	
	// Function to process My Access data
	const processMyAccess = useCallback(async () => {
		if (!user?.email || !users || users.length === 0) {
			setMyAccess([]);
			setMyAccessLoaded(false);
			return [];
		}
		
		// Filter users data for the current user's email
		const myAccessData = users.filter(item => 
			item.email_address && 
			item.email_address.toLowerCase() === user.email.toLowerCase()
		);
		
		// Update state with found data
		setMyAccess(myAccessData);
		setMyAccessLoaded(true);
		
		return myAccessData;
	}, [users, user]);
	
	// Function to fetch My Access data directly from API
	const fetchMyAccess = useCallback(async (forceRefresh = false) => {
		// Don't fetch if no user email
		if (!user?.email) {
			setMyAccessLoaded(false);
			setMyAccessError(new Error('User email not available'));
			return [];
		}
		
		// Prevent concurrent requests
		if (isLoadingMyAccess.current) {
			return myAccess;
		}
		
		// Check if we already have the data and can return it
		if (myAccessLoaded && myAccess !== null && myAccess.length > 0 && !forceRefresh) {
			return myAccess;
		}
		
		// Check cache if not forcing refresh
		if (!forceRefresh) {
			const cachedMyAccess = getCachedData('googleAdsMyAccess');
			if (cachedMyAccess && cachedMyAccess.length > 0) {
				setMyAccess(cachedMyAccess);
				setMyAccessLoaded(true);
				setMyAccessError(null);
				return cachedMyAccess;
			}
		}
		
		// Set loading flag
		isLoadingMyAccess.current = true;
		
		// Track performance
		const startTime = performance.now();
		
		try {
			const myAccessData = await fetchMyAccessFromApi(user.email, forceRefresh);
			
			// Log performance
			const duration = performance.now() - startTime;
			console.log(`GoogleAdsContext: Fetched ${myAccessData.length} My Access entries in ${duration.toFixed(2)}ms`);
			
			// Update state
			setMyAccess(myAccessData);
			setMyAccessLoaded(true);
			setMyAccessError(null);
			
			// Cache the data
			cacheData('googleAdsMyAccess', myAccessData);
			
			return myAccessData;
		} catch (error) {
			console.error('GoogleAdsContext: Error fetching My Access data:', error);
			setMyAccessError(error.message || 'Failed to fetch My Access data');
			
			// Fall back to filtering users data if available
			if (usersLoaded && users.length > 0) {
				return processMyAccess();
			}
			
			return [];
		} finally {
			isLoadingMyAccess.current = false;
		}
	}, [user, myAccess, myAccessLoaded, users, usersLoaded, processMyAccess]);
	
	// Function to fetch pending requests
	const fetchPendingRequests = useCallback(async (forceRefresh = false) => {
		// Prevent concurrent requests
		if (isLoadingPendingRequests.current) {
			return pendingRequests;
		}
		
		// Check if we already have the data and can return it
		if (pendingRequestsLoaded && pendingRequests !== null && !forceRefresh) {
			return pendingRequests;
		}
		
		// Check cache if not forcing refresh
		if (!forceRefresh) {
			const cachedRequests = getCachedData('googleAdsPendingRequests');
			if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
				setPendingRequests(cachedRequests);
				setPendingRequestsLoaded(true);
				setPendingRequestsError(null);
				return cachedRequests;
			}
		}
		
		// Set loading flag
		isLoadingPendingRequests.current = true;
		
		// Track performance
		const startTime = performance.now();
		
		try {
			const requestsData = await fetchPendingRequestsFromApi(forceRefresh);
			
			// Log performance
			const duration = performance.now() - startTime;
			console.log(`GoogleAdsContext: Fetched ${requestsData.length} pending requests in ${duration.toFixed(2)}ms`);
			
			// Update state
			setPendingRequests(requestsData);
			setPendingRequestsLoaded(true);
			setPendingRequestsError(null);
			
			// Cache the data
			cacheData('googleAdsPendingRequests', requestsData);
			
			return requestsData;
		} catch (error) {
			console.error('GoogleAdsContext: Error fetching pending requests:', error);
			setPendingRequestsError(error.message || 'Failed to fetch pending requests');
			return [];
		} finally {
			isLoadingPendingRequests.current = false;
		}
	}, [pendingRequests, pendingRequestsLoaded]);
	
	// Function to fetch approved requests
	const fetchApprovedRequests = useCallback(async (forceRefresh = false) => {
		// Prevent concurrent requests
		if (isLoadingApprovedRequests.current) {
			return approvedRequests;
		}
		
		// Check if we already have the data and can return it
		if (approvedRequestsLoaded && approvedRequests !== null && !forceRefresh) {
			return approvedRequests;
		}
		
		// Check cache if not forcing refresh
		if (!forceRefresh) {
			const cachedRequests = getCachedData('googleAdsApprovedRequests');
			if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
				setApprovedRequests(cachedRequests);
				setApprovedRequestsLoaded(true);
				setApprovedRequestsError(null);
				return cachedRequests;
			}
		}
		
		// Set loading flag
		isLoadingApprovedRequests.current = true;
		
		// Track performance
		const startTime = performance.now();
		
		try {
			const requestsData = await fetchApprovedRequestsFromApi(forceRefresh);
			
			// Log performance
			const duration = performance.now() - startTime;
			console.log(`GoogleAdsContext: Fetched ${requestsData.length} approved requests in ${duration.toFixed(2)}ms`);
			
			// Update state
			setApprovedRequests(requestsData);
			setApprovedRequestsLoaded(true);
			setApprovedRequestsError(null);
			
			// Cache the data
			cacheData('googleAdsApprovedRequests', requestsData);
			
			return requestsData;
		} catch (error) {
			console.error('GoogleAdsContext: Error fetching approved requests:', error);
			setApprovedRequestsError(error.message || 'Failed to fetch approved requests');
			return [];
		} finally {
			isLoadingApprovedRequests.current = false;
		}
	}, [approvedRequests, approvedRequestsLoaded]);
	
	// Function to fetch rejected requests
	const fetchRejectedRequests = useCallback(async (forceRefresh = false) => {
		// Prevent concurrent requests
		if (isLoadingRejectedRequests.current) {
			return rejectedRequests;
		}
		
		// Check if we already have the data and can return it
		if (rejectedRequestsLoaded && rejectedRequests !== null && !forceRefresh) {
			return rejectedRequests;
		}
		
		// Check cache if not forcing refresh
		if (!forceRefresh) {
			const cachedRequests = getCachedData('googleAdsRejectedRequests');
			if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
				setRejectedRequests(cachedRequests);
				setRejectedRequestsLoaded(true);
				setRejectedRequestsError(null);
				return cachedRequests;
			}
		}
		
		// Set loading flag
		isLoadingRejectedRequests.current = true;
		
		// Track performance
		const startTime = performance.now();
		
		try {
			const requestsData = await fetchRejectedRequestsFromApi(forceRefresh);
			
			// Log performance
			const duration = performance.now() - startTime;
			console.log(`GoogleAdsContext: Fetched ${requestsData.length} rejected requests in ${duration.toFixed(2)}ms`);
			
			// Update state
			setRejectedRequests(requestsData);
			setRejectedRequestsLoaded(true);
			setRejectedRequestsError(null);
			
			// Cache the data
			cacheData('googleAdsRejectedRequests', requestsData);
			
			return requestsData;
		} catch (error) {
			console.error('GoogleAdsContext: Error fetching rejected requests:', error);
			setRejectedRequestsError(error.message || 'Failed to fetch rejected requests');
			return [];
		} finally {
			isLoadingRejectedRequests.current = false;
		}
	}, [rejectedRequests, rejectedRequestsLoaded]);
	
	// Function to refresh all data at once
	const refreshAll = useCallback(async (forceRefresh = true) => {
		// Track performance
		const startTime = performance.now();
		
		// Run all data refreshes in parallel for better performance
		const promises = [
			fetchUsers(forceRefresh),
			fetchInvitations(forceRefresh),
			fetchTopLevelMccAccounts(forceRefresh),
			fetchAdsManagedAccounts(forceRefresh),
			fetchAuditLogs(forceRefresh),
			fetchMyAccess(forceRefresh),
			fetchPendingRequests(forceRefresh),
			fetchApprovedRequests(forceRefresh),
			fetchRejectedRequests(forceRefresh)
		];
		
		// Wait for all to complete
		await Promise.all(promises);
		
		// Log performance
		const duration = performance.now() - startTime;
		console.log(`GoogleAdsContext: Refreshed all Google Ads data in ${duration.toFixed(2)}ms`);
		
		return true;
	}, [fetchUsers, fetchInvitations, fetchTopLevelMccAccounts, fetchAdsManagedAccounts, fetchAuditLogs, fetchMyAccess, fetchPendingRequests, fetchApprovedRequests, fetchRejectedRequests]);
	
	// Eager load data when user is authenticated
	useEffect(() => {
		// Skip if no user or already loaded
		if (!isAuthenticated || !user?.email || hasLoadedInitialData.current) {
			return;
		}

		// Wait for permissions to load, then check if user has ads admin access
		if (permissionsLoading) {
			console.log('GoogleAdsContext: Waiting for permissions to load...');
			return;
		}
		  
		// If user doesn't have ads admin permission, skip all data fetching
		if (!hasAdsPermission) {
			console.log('GoogleAdsContext: User lacks admin/ads permission, skipping data fetch');
			
			// Set flag to prevent re-triggering
			hasLoadedInitialData.current = true;
			
			// Mark all data as "loaded" with empty values to prevent loading spinners
			setUsersLoaded(true);
			setInvitationsLoaded(true);
			setTopLevelMccAccountsLoaded(true);
			setAdsManagedAccountsLoaded(true);
			setAuditLogsLoaded(true);
			setMyAccessLoaded(true);
			setPendingRequestsLoaded(true);
			setApprovedRequestsLoaded(true);
			setRejectedRequestsLoaded(true);
			
			// Exit early so no API calls will be made
			return; 
		}

		const loadInitialData = async () => {
			try {
				// Set flag to prevent multiple loads
				hasLoadedInitialData.current = true;
				
				// Track performance
				const startTime = performance.now();
				
				// Check if we have any cached data first
				const cachedUsers = getCachedData('googleAdsUsers');
				const cachedInvitations = getCachedData('googleAdsInvitations');
				const cachedAccounts = getCachedData('googleAdsTopLevelAccounts');
				const cachedManagedAccounts = getCachedData('googleAdsManagedAccounts');
				const cachedAuditLogs = getCachedData('googleAdsAuditLogs');
				const cachedMyAccess = getCachedData('googleAdsMyAccess');
				const cachedPendingRequests = getCachedData('googleAdsPendingRequests');
				const cachedApprovedRequests = getCachedData('googleAdsApprovedRequests');
				const cachedRejectedRequests = getCachedData('googleAdsRejectedRequests');
				
				// Set any cached data we find to prevent flash of loading state
				if (cachedUsers?.length > 0) {
					setUsers(cachedUsers);
					setUsersLoaded(true);
				}
				
				if (cachedInvitations?.length > 0) {
					setInvitations(cachedInvitations);
					setInvitationsLoaded(true);
				}
				
				if (cachedAccounts?.length > 0) {
					setTopLevelMccAccounts(cachedAccounts);
					setTopLevelMccAccountsLoaded(true);
				}

				if (cachedManagedAccounts?.length > 0) {
					setAdsManagedAccounts(cachedManagedAccounts);
					setAdsManagedAccountsLoaded(true);
				}
				
				if (cachedAuditLogs?.length > 0) {
					setAuditLogs(cachedAuditLogs);
					setAuditLogsLoaded(true);
				}
				
				if (cachedMyAccess?.length !== undefined) {
					setMyAccess(cachedMyAccess || []);
					setMyAccessLoaded(true);
				}
				
				if (cachedPendingRequests?.length !== undefined) {
					setPendingRequests(cachedPendingRequests || []);
					setPendingRequestsLoaded(true);
				}
				
				if (cachedApprovedRequests?.length !== undefined) {
					setApprovedRequests(cachedApprovedRequests || []);
					setApprovedRequestsLoaded(true);
				}
				
				if (cachedRejectedRequests?.length !== undefined) {
					setRejectedRequests(cachedRejectedRequests || []);
					setRejectedRequestsLoaded(true);
				}
				
				// Always fetch fresh data in the background, regardless of cache
				const loadWithTimeout = async (fetchFn, dataType, timeoutMs = 90000) => {
					try {
						const timeoutPromise = new Promise((_, reject) => {
							const timeoutId = setTimeout(() => {
								clearTimeout(timeoutId);
								console.warn(`GoogleAdsContext: ${dataType} data loading approaching timeout (${timeoutMs/1000}s) - will continue in background`);
								reject({ isTimeout: true, message: `Timeout reached for ${dataType}` });
							}, timeoutMs);
						});
						
						try {
							const result = await Promise.race([fetchFn(true), timeoutPromise]);
							return result;
						} catch (err) {
							if (err?.isTimeout) {
								return null;
							}
							throw err;
						}
					} catch (err) {
						console.error(`GoogleAdsContext: Error loading ${dataType} data:`, err);
						return null;
					}
				};
				
				// Load all data types in parallel
				const promises = [
					loadWithTimeout(fetchUsers, 'users'),
					loadWithTimeout(fetchInvitations, 'invitations'),
					loadWithTimeout(fetchTopLevelMccAccounts, 'accounts'),
					loadWithTimeout(fetchAuditLogs, 'auditLogs'),
					loadWithTimeout(fetchMyAccess, 'myAccess'),
					loadWithTimeout(fetchPendingRequests, 'pendingRequests'),
					loadWithTimeout(fetchApprovedRequests, 'approvedRequests'),
					loadWithTimeout(fetchRejectedRequests, 'rejectedRequests'),
					loadWithTimeout(fetchAdsManagedAccounts, 'managedAccounts')
				];
				
				// Wait for all data types to either load or timeout
				const results = await Promise.allSettled(promises);
				
				// For my access, even if the array is empty, consider it loaded
				if (results[4].status === 'fulfilled' && results[4].value !== null) {
					setMyAccess(results[4].value || []);
					setMyAccessLoaded(true);
				}

				// For pending requests, even if the array is empty, consider it loaded
				if (results[5].status === 'fulfilled' && results[5].value !== null) {
					setPendingRequests(results[5].value || []);
					setPendingRequestsLoaded(true);
				}

				// For approved requests, even if the array is empty, consider it loaded
				if (results[6].status === 'fulfilled' && results[6].value !== null) {
					setApprovedRequests(results[6].value || []);
					setApprovedRequestsLoaded(true);
				}

				// For rejected requests, even if the array is empty, consider it loaded
				if (results[7].status === 'fulfilled' && results[7].value !== null) {
					setRejectedRequests(results[7].value || []);
					setRejectedRequestsLoaded(true);
				}
				
				// Log any failed requests
				results.forEach((result, index) => {
					if (result.status === 'rejected') {
						const dataType = ['users', 'invitations', 'accounts', 'auditLogs', 'myAccess', 'pendingRequests', 'approvedRequests', 'rejectedRequests', 'managedAccounts'][index];
						console.error(`GoogleAdsContext: Failed to fetch ${dataType} data:`, result.reason);
					}
				});
				
				// Log performance
				const duration = performance.now() - startTime;
				console.log(`GoogleAdsContext: Initial data loading complete in ${duration.toFixed(2)}ms`);
			} catch (err) {
				console.error('GoogleAdsContext: Error during initial data loading:', err);
			}
		};
		
		// Start loading data
		loadInitialData();
	}, [isAuthenticated, user?.email, permissionsLoading, hasAdsPermission, fetchUsers, fetchInvitations, fetchTopLevelMccAccounts, fetchAdsManagedAccounts, fetchAuditLogs, fetchMyAccess, fetchPendingRequests, fetchApprovedRequests, fetchRejectedRequests]);
	
	// Separate effect to mark context as initialized when auth state is available
	useEffect(() => {
		if (!initialized && isAuthenticated !== null) {
			console.log('GoogleAdsContext: Context initialized with auth state:', isAuthenticated ? 'authenticated' : 'not authenticated');
			setInitialized(true);
		}
	}, [isAuthenticated, initialized]);
	
	// Prepare context value
	const contextValue = {
		// User data
		users,
		usersLoaded,
		usersError,
		
		// Invitations
		invitations,
		invitationsLoaded,
		invitationsError,
		
		// Top-level MCC accounts
		topLevelMccAccounts,
		topLevelMccAccountsLoaded,
		topLevelMccAccountsError,

		// Managed accounts
		adsManagedAccounts,
		adsManagedAccountsLoaded,
		adsManagedAccountsError,
		
		// Audit logs
		auditLogs,
		auditLogsLoaded,
		auditLogsError,
		
		// My access
		myAccess,
		myAccessLoaded,
		myAccessError,
		
		// Pending requests
		pendingRequests,
		pendingRequestsLoaded,
		pendingRequestsError,
		
		// Approved requests
		approvedRequests,
		approvedRequestsLoaded,
		approvedRequestsError,
		
		// Rejected requests
		rejectedRequests,
		rejectedRequestsLoaded,
		rejectedRequestsError,
		
		// Actions
		fetchUsers,
		fetchInvitations,
		fetchTopLevelMccAccounts,
		fetchAdsManagedAccounts,
		fetchAuditLogs,
		fetchMyAccess,
		fetchPendingRequests,
		fetchApprovedRequests,
		fetchRejectedRequests,
		processMyAccess,
		refreshAll,
		
		// Context state
		initialized
	};
	
	// Render provider with value
	return (
		<GoogleAdsContext.Provider value={contextValue}>
			{children}
		</GoogleAdsContext.Provider>
	);
};

// Custom hook to use the Google Ads context
export const useGoogleAds = () => {
	const context = useContext(GoogleAdsContext);
	if (!context) {
		throw new Error('useGoogleAds must be used within a GoogleAdsProvider');
	}
	return context;
}; 

export default GoogleAdsContext;
